/**
 * CBO Profile Modal
 * Detailed view of a CBO's capabilities, services, and performance
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Users,
  Award,
  TrendingUp,
  CheckCircle2,
  Target,
  Heart,
  Network,
  Calendar,
  DollarSign
} from 'lucide-react';

interface CBOProfileModalProps {
  cboId: number;
  isOpen: boolean;
  onClose: () => void;
  onInvite?: () => void;
}

interface CBOProfile {
  id: number;
  name: string;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  phone?: string;
  email?: string;
  website?: string;
  mission_statement?: string;
  founded_year?: number;
  service_area?: string;
  populations_served?: string[];
  services_offered?: string[];
  capacity_available?: number;
  active_members?: number;
  success_rate?: number;
  years_in_operation?: number;
  certifications?: string[];
  languages_supported?: string[];
}

export const CBOProfileModal: React.FC<CBOProfileModalProps> = ({
  cboId,
  isOpen,
  onClose,
  onInvite
}) => {
  const { t } = useAppTranslation();
  const [profile, setProfile] = useState<CBOProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && cboId) {
      fetchCBOProfile();
    }
  }, [isOpen, cboId]);

  const fetchCBOProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(getWeaveinApiUrl(`/api/cbo/${cboId}`));

      if (!response.ok) {
        throw new Error('Failed to fetch CBO profile');
      }

      const data = await response.json();
      setProfile(data);
    } catch (err) {
      console.error('Error fetching CBO profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  if (!profile && !loading) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="large" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={fetchCBOProfile}>{t('marketplace.try_again', 'Try Again')}</Button>
          </div>
        ) : profile ? (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <DialogTitle className="text-3xl font-bold text-gray-900 mb-2">
                    {profile.name}
                  </DialogTitle>
                  <DialogDescription>
                    {profile.mission_statement || 'View detailed CBO profile information'}
                  </DialogDescription>
                </div>
                {onInvite && (
                  <Button onClick={onInvite} className="flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    {t('marketplace.invite_to_program', 'Invite to Program')}
                  </Button>
                )}
              </div>
            </DialogHeader>

            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    <span className="text-sm text-blue-600">{t('marketplace.capacity', 'Capacity')}</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">
                    {profile.capacity_available || 'N/A'}
                  </p>
                </Card>

                <Card className="p-4 bg-green-50 border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-600">{t('marketplace.active_members', 'Active Members')}</span>
                  </div>
                  <p className="text-2xl font-bold text-green-900">
                    {profile.active_members || 'N/A'}
                  </p>
                </Card>

                <Card className="p-4 bg-purple-50 border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                    <span className="text-sm text-purple-600">{t('marketplace.success_rate', 'Success Rate')}</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-900">
                    {profile.success_rate ? `${profile.success_rate}%` : 'N/A'}
                  </p>
                </Card>

                <Card className="p-4 bg-orange-50 border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-5 h-5 text-orange-600" />
                    <span className="text-sm text-orange-600">{t('marketplace.experience', 'Experience')}</span>
                  </div>
                  <p className="text-2xl font-bold text-orange-900">
                    {profile.years_in_operation || profile.founded_year
                      ? `${profile.years_in_operation || new Date().getFullYear() - profile.founded_year!} yrs`
                      : 'N/A'}
                  </p>
                </Card>
              </div>

              {/* Contact Information */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-gray-600" />
                {t('marketplace.contact_information', 'Contact Information')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {profile.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">{t('marketplace.address', 'Address')}</p>
                      <p className="font-medium text-gray-900">{profile.address}</p>
                      {(profile.city || profile.state || profile.zip_code) && (
                        <p className="text-sm text-gray-600">
                          {[profile.city, profile.state, profile.zip_code].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {profile.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">{t('marketplace.phone', 'Phone')}</p>
                      <p className="font-medium text-gray-900">{formatPhone(profile.phone)}</p>
                    </div>
                  </div>
                )}

                {profile.email && (
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">{t('marketplace.email', 'Email')}</p>
                      <a href={`mailto:${profile.email}`} className="font-medium text-blue-600 hover:underline">
                        {profile.email}
                      </a>
                    </div>
                  </div>
                )}

                {profile.website && (
                  <div className="flex items-start gap-3">
                    <Globe className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">{t('marketplace.website', 'Website')}</p>
                      <a
                        href={profile.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-600 hover:underline"
                      >
                        {t('marketplace.visit_website', 'Visit Website')}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Services Offered */}
            {profile.services_offered && profile.services_offered.length > 0 && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-gray-600" />
                  {t('marketplace.services_offered', 'Services Offered')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {profile.services_offered.map((service, index) => (
                    <Badge key={index} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {service}
                    </Badge>
                  ))}
                </div>
              </Card>
            )}

            {/* Populations Served */}
            {profile.populations_served && profile.populations_served.length > 0 && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-600" />
                  {t('marketplace.populations_served', 'Populations Served')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {profile.populations_served.map((population, index) => (
                    <Badge key={index} variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                      {population}
                    </Badge>
                  ))}
                </div>
              </Card>
            )}

            {/* Certifications */}
            {profile.certifications && profile.certifications.length > 0 && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-gray-600" />
                  {t('marketplace.certifications_accreditations', 'Certifications & Accreditations')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {profile.certifications.map((cert, index) => (
                    <Badge key={index} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      {cert}
                    </Badge>
                  ))}
                </div>
              </Card>
            )}

            {/* Languages Supported */}
            {profile.languages_supported && profile.languages_supported.length > 0 && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-gray-600" />
                  {t('marketplace.languages_supported', 'Languages Supported')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {profile.languages_supported.map((lang, index) => (
                    <Badge key={index} variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                      {lang}
                    </Badge>
                  ))}
                </div>
              </Card>
            )}

            {/* Description */}
            {profile.description && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">{t('marketplace.about', 'About')}</h3>
                <p className="text-gray-700 leading-relaxed">{profile.description}</p>
              </Card>
            )}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default CBOProfileModal;
